import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AgendaItem, useInstructorAgenda } from "../api/instructorAgenda";
import { useAuth } from "../auth/AuthContext";
import { CancelAppointmentModal } from "../components/calendar/CancelAppointmentModal";
import { ReactivateAppointmentModal } from "../components/calendar/ReactivateAppointmentModal";
import LessonReportModal from "../components/lesson-reports/LessonReportModal";
import { WhatsAppLink } from "../components/WhatsAppLink";
import { UI_LOADING } from "../constants/uiLabels";
import "./InstructorMobilePage.css";

type ViewMode = "today" | "tomorrow" | "week";

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function shiftIso(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

function fmtTime(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" });
}

function fmtDay(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString("tr-TR", { weekday: "long", day: "numeric", month: "long" });
}

function fmtMinutes(min: number): string {
  if (!min) return "0 dk";
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (h === 0) return `${m} dk`;
  if (m === 0) return `${h} sa`;
  return `${h} sa ${m} dk`;
}

const STATUS_LABELS: Record<string, { label: string; tone: "ok" | "warn" | "info" | "muted" }> = {
  planned: { label: "Planlandi", tone: "info" },
  in_progress: { label: "Devam Ediyor", tone: "warn" },
  completed: { label: "Tamamlandi", tone: "ok" },
  cancelled: { label: "İptal", tone: "muted" },
};

const LESSON_TYPE_LABELS: Record<string, string> = {
  normal: "Normal",
  special: "Ozel",
  extra: "Ek",
};

export default function InstructorMobilePage() {
  const { session } = useAuth();
  const [view, setView] = useState<ViewMode>("today");
  const [openingReport, setOpeningReport] = useState<AgendaItem | null>(null);
  const [cancelling, setCancelling] = useState<AgendaItem | null>(null);
  const [reactivating, setReactivating] = useState<AgendaItem | null>(null);

  const { startDate, endDate } = useMemo(() => {
    if (view === "today") return { startDate: todayIso(), endDate: todayIso() };
    if (view === "tomorrow") return { startDate: shiftIso(1), endDate: shiftIso(1) };
    return { startDate: todayIso(), endDate: shiftIso(6) };
  }, [view]);

  const { data, loading, error, reload } = useInstructorAgenda(startDate, endDate);

  // Group items by day for week view
  const grouped = useMemo(() => {
    if (!data) return new Map<string, AgendaItem[]>();
    const map = new Map<string, AgendaItem[]>();
    for (const item of data.items) {
      const key = item.starts_at.slice(0, 10);
      const arr = map.get(key) || [];
      arr.push(item);
      map.set(key, arr);
    }
    return map;
  }, [data]);

  return (
    <div className="m-page">
      <header className="m-header">
        <div>
          <span className="m-header__hello">Merhaba</span>
          <strong className="m-header__name">{session?.user?.full_name || "Eğitmen"}</strong>
        </div>
        <Link to="/calendar" className="m-header__pc-link">
          PC Görünümü
        </Link>
      </header>

      <nav className="m-tabs">
        <button type="button" className={view === "today" ? "is-active" : ""} onClick={() => setView("today")}>
          Bugun
        </button>
        <button type="button" className={view === "tomorrow" ? "is-active" : ""} onClick={() => setView("tomorrow")}>
          Yarin
        </button>
        <button type="button" className={view === "week" ? "is-active" : ""} onClick={() => setView("week")}>
          7 Gun
        </button>
      </nav>

      {data && (
        <section className="m-summary">
          <div className="m-stat">
            <div className="m-stat__num">{data.summary.planned}</div>
            <div className="m-stat__label">Planli</div>
          </div>
          <div className="m-stat">
            <div className="m-stat__num">{data.summary.completed}</div>
            <div className="m-stat__label">Tamam</div>
          </div>
          <div className="m-stat m-stat--warn">
            <div className="m-stat__num">{data.summary.pending_report}</div>
            <div className="m-stat__label">Rapor Bekleyen</div>
          </div>
          <div className="m-stat">
            <div className="m-stat__num">{fmtMinutes(data.summary.total_completed_minutes)}</div>
            <div className="m-stat__label">Tamamlanan Sure</div>
          </div>
        </section>
      )}

      {loading && <div className="m-info">{UI_LOADING}</div>}
      {error && <div className="m-error">{error}</div>}

      {data && data.items.length === 0 && !loading && (
        <div className="m-empty">
          <div className="m-empty__icon">~</div>
          <div className="m-empty__text">Bu Dönemde randevunuz bulunmuyor.</div>
        </div>
      )}

      {data && data.items.length > 0 && view !== "week" && (
        <div className="m-list">
          {data.items.map((item) => (
            <AgendaCard
              key={item.appointment_id}
              item={item}
              onOpenReport={() => setOpeningReport(item)}
              onCancel={() => setCancelling(item)}
              onReactivate={() => setReactivating(item)}
            />
          ))}
        </div>
      )}

      {data && data.items.length > 0 && view === "week" && (
        <div className="m-week">
          {Array.from(grouped.entries()).map(([day, items]) => (
            <section key={day} className="m-week__day">
              <h3 className="m-week__day-title">{fmtDay(day)}</h3>
              <div className="m-list">
                {items.map((item) => (
                  <AgendaCard
                    key={item.appointment_id}
                    item={item}
                    onOpenReport={() => setOpeningReport(item)}
                    onCancel={() => setCancelling(item)}
                    onReactivate={() => setReactivating(item)}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      {openingReport && (
        <LessonReportModal
          appointmentId={openingReport.appointment_id}
          studentName={openingReport.student_name}
          vehiclePlate={openingReport.vehicle_plate}
          startsAt={openingReport.starts_at}
          endsAt={openingReport.ends_at}
          onClose={() => setOpeningReport(null)}
          onSaved={() => {
            reload();
            setOpeningReport(null);
          }}
        />
      )}

      {cancelling && (
        <CancelAppointmentModal
          appointmentId={cancelling.appointment_id}
          studentName={cancelling.student_name}
          startsAt={cancelling.starts_at}
          endsAt={cancelling.ends_at}
          onClose={() => setCancelling(null)}
          onDeleted={() => {
            reload();
            setCancelling(null);
          }}
          onCancelled={() => {
            reload();
            setCancelling(null);
          }}
        />
      )}

      {reactivating && (
        <ReactivateAppointmentModal
          appointmentId={reactivating.appointment_id}
          studentName={reactivating.student_name}
          startsAt={reactivating.starts_at}
          endsAt={reactivating.ends_at}
          onClose={() => setReactivating(null)}
          onReactivated={() => {
            reload();
            setReactivating(null);
          }}
        />
      )}
    </div>
  );
}

function AgendaCard({
  item,
  onOpenReport,
  onCancel,
  onReactivate,
}: {
  item: AgendaItem;
  onOpenReport: () => void;
  onCancel?: () => void;
  onReactivate?: () => void;
}) {
  const status = STATUS_LABELS[item.status] || { label: item.status, tone: "muted" as const };
  const lessonLabel = LESSON_TYPE_LABELS[item.lesson_type] || item.lesson_type;

  const isCancelled = item.status === "cancelled";
  const canCancel =
    !isCancelled && item.status !== "completed" && typeof onCancel === "function";
  const canReactivate = isCancelled && typeof onReactivate === "function";
  const canReport = !isCancelled;
  const reportLabel = item.has_report ? "Raporu Düzenle" : "Ders Raporu";
  const phoneHref = item.student_phone ? `tel:${item.student_phone}` : null;
  const wpHref = item.student_phone
    ? `https://wa.me/${item.student_phone.replace(/[^0-9]/g, "")}`
    : null;

  return (
    <article className="m-card">
      <header className="m-card__head">
        <div className="m-card__time">
          <strong>{fmtTime(item.starts_at)}</strong>
          <span className="m-card__time-sep">-</span>
          <span>{fmtTime(item.ends_at)}</span>
          <span className="m-card__duration">· {fmtMinutes(item.duration_minutes)}</span>
        </div>
        <span className={`m-badge m-badge--${status.tone}`}>{status.label}</span>
      </header>

      <div className="m-card__row">
        <div className="m-card__icon" aria-hidden>
          K
        </div>
        <div className="m-card__main">
          <strong className="m-card__title">{item.student_name}</strong>
          {item.student_exam_date && (
            <div className="m-card__sub">Direksiyon: {item.student_exam_date}</div>
          )}
        </div>
        <div className="m-card__contacts">
          {phoneHref && (
            <a href={phoneHref} className="m-iconbtn" aria-label="Ara">
              Ara
            </a>
          )}
          {wpHref && <WhatsAppLink href={wpHref} className="m-iconbtn m-iconbtn--wp" size={18} />}
        </div>
      </div>

      <div className="m-card__row m-card__row--muted">
        <div className="m-card__veh">
          <span className="mono">{item.vehicle_plate}</span>
          {item.vehicle_brand_model && <span> · {item.vehicle_brand_model}</span>}
          {item.vehicle_gear_type && (
            <span> · {item.vehicle_gear_type === "automatic" ? "Otomatik" : "Manuel"}</span>
          )}
        </div>
        <div className="m-card__km">
          <span className="m-card__km-label">Guncel KM</span>
          <strong className="mono">{item.vehicle_current_km.toLocaleString("tr-TR")}</strong>
        </div>
      </div>

      <div className="m-card__row m-card__row--meta">
        <span className={`m-tag m-tag--${item.lesson_type}`}>{lessonLabel}</span>
        {item.has_report ? (
          <span className="m-tag m-tag--ok">
            Rapor: {item.report_attended === false ? "Gelmedi" : "Tamam"}
            {item.report_driving_score !== null && ` · ${item.report_driving_score}/5`}
            {item.report_end_km !== null && item.report_start_km !== null && (
              <> · +{item.report_end_km - item.report_start_km} km</>
            )}
          </span>
        ) : (
          <span className="m-tag m-tag--warn">Rapor Bekliyor</span>
        )}
      </div>

      <div className="m-card__actions">
        {canReactivate && (
          <button type="button" className="m-btn m-btn--reactivate" onClick={onReactivate}>
            Dersi Yeniden Aktifleştir
          </button>
        )}
        {canCancel && (
          <button type="button" className="m-btn m-btn--danger-outline" onClick={onCancel}>
            Dersi İptal Et
          </button>
        )}
        <button type="button" className="m-btn" onClick={onOpenReport} disabled={!canReport}>
          {reportLabel}
        </button>
      </div>
    </article>
  );
}
